import { useEffect, useState, useCallback, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { fmtDate, fmtTons, fmtMoney, monthLabel } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CustomerAutocomplete from "@/components/CustomerAutocomplete";
import { toast } from "sonner";
import { Printer, Copy, FileText, Save, Pencil, X, Trash2, Plus, CheckCircle2, AlertTriangle } from "lucide-react";

export default function Billing() {
  const [searchParams] = useSearchParams();
  const [customer, setCustomer] = useState(searchParams.get("customer") || "");
  const [month, setMonth] = useState(searchParams.get("month") || new Date().toISOString().slice(0, 7));
  const [preview, setPreview] = useState(null); // {lines,total_tons,previous_month,previous_bill_total,has_previous_saved,saved}
  const [rate, setRate] = useState("");
  const [includePrevious, setIncludePrevious] = useState(false);
  const [includeOld, setIncludeOld] = useState(false);
  const [oldBalance, setOldBalance] = useState("");
  const [includeDiscount, setIncludeDiscount] = useState(false);
  const [discount, setDiscount] = useState("");
  const [savedBill, setSavedBill] = useState(null); // persisted snapshot
  const [loading, setLoading] = useState(false);
  const [edit, setEdit] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [includedMonths, setIncludedMonths] = useState([]);
  const [includeGst, setIncludeGst] = useState(false);
  const [gstType, setGstType] = useState("exclusive");
  const [gstRate, setGstRate] = useState("");
  const [settings, setSettings] = useState(null);
  const emptyAdd = { sale_date: new Date().toISOString().slice(0, 10), tons: "", vehicle_no: "", plant: "SFX 1" };
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(emptyAdd);

  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  const addSale = async () => {
    if (!customer.trim()) { toast.error("Select a customer first"); return; }
    if (!addForm.tons) { toast.error("Enter quantity"); return; }
    try {
      await api.post("/sales", {
        sale_date: addForm.sale_date, customer_name: customer.trim(),
        tons: Number(addForm.tons), vehicle_no: addForm.vehicle_no, plant: addForm.plant,
      });
      toast.success("Sale added");
      setAddOpen(false); setAddForm(emptyAdd); setSavedBill(null);
      await refetchPreview();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveLine = async () => {
    try {
      await api.put(`/sales/${edit.id}`, { sale_date: edit.sale_date, tons: Number(edit.tons), vehicle_no: edit.vehicle_no });
      toast.success("Sale updated");
      setEdit(null);
      setSavedBill(null);
      await refetchPreview();
    } catch (e) { toast.error("Update failed"); }
  };

  const deleteLine = async () => {
    try {
      await api.delete(`/sales/${delTarget.id}`);
      toast.success("Transaction deleted");
      setDelTarget(null);
      setSavedBill(null);
      await refetchPreview();
    } catch (e) { toast.error("Delete failed"); }
  };

  // Refresh only the preview lines/totals; keep admin-entered rate & toggles intact.
  const refetchPreview = useCallback(async () => {
    if (!customer.trim()) return;
    try {
      const { data } = await api.get("/bills/preview", { params: { customer: customer.trim(), month, include_months: includedMonths.join(",") || undefined } });
      setPreview(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [customer, month, includedMonths]);

  // Bootstrap: fetch preview + restore saved-bill config. Runs on customer/month change only.
  const loadPreview = useCallback(async () => {
    if (!customer.trim()) { setPreview(null); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/bills/preview", { params: { customer: customer.trim(), month } });
      setPreview(data);
      if (data.saved) {
        setSavedBill(data.saved);
        setRate(String(data.saved.rate_per_ton || ""));
        setIncludePrevious(!!data.saved.include_previous || (data.saved.included_months || []).length > 0);
        setIncludedMonths(data.saved.included_months || []);
        setIncludeOld(!!data.saved.include_old_balance);
        setOldBalance(data.saved.old_balance ? String(data.saved.old_balance) : "");
        setIncludeDiscount(!!data.saved.include_discount);
        setDiscount(data.saved.discount ? String(data.saved.discount) : "");
        setIncludeGst(!!data.saved.include_gst);
        setGstType(data.saved.gst_type || "exclusive");
        setGstRate(data.saved.gst_rate ? String(data.saved.gst_rate) : "");
      } else {
        setSavedBill(null);
        setRate(""); setIncludePrevious(false); setIncludedMonths([]); setIncludeOld(false); setOldBalance("");
        setIncludeDiscount(false); setDiscount("");
        setIncludeGst(false); setGstType("exclusive"); setGstRate("");
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [customer, month]);

  useEffect(() => { loadPreview(); }, [loadPreview]);
  // Refetch preview lines whenever the included-months selection changes (no form reset).
  useEffect(() => { refetchPreview(); /* eslint-disable-next-line */ }, [includedMonths]);

  const invalidate = () => setSavedBill(null);
  const addMonth = (m) => { if (m && !includedMonths.includes(m)) { setIncludedMonths([...includedMonths, m]); invalidate(); } };
  const removeMonth = (m) => { setIncludedMonths(includedMonths.filter((x) => x !== m)); invalidate(); };

  const r2 = (n) => Math.round(n * 100) / 100;
  const r3 = (n) => Math.round(n * 1000) / 1000;
  const rateNum = Number(rate || 0);
  const currentMonthTons = r3((preview?.lines || []).reduce((s, l) => s + l.tons, 0));

  const curLines = (preview?.lines || []).map((l) => ({ ...l, amount: r2(l.tons * rateNum) }));
  const incLines = [];
  (preview?.included || []).forEach((g) => g.lines.forEach((l) => incLines.push({ ...l, month: g.month, amount: r2(l.tons * rateNum) })));
  const plantRank = (p) => (p === "SFX 1" ? 0 : p === "SFX 2" ? 1 : 2);
  const allLines = [...curLines, ...incLines].sort((a, b) => {
    const pr = plantRank(a.plant) - plantRank(b.plant);
    if (pr !== 0) return pr;
    return a.sale_date < b.sale_date ? -1 : a.sale_date > b.sale_date ? 1 : 0;
  });
  const totalTons = r3(allLines.reduce((s, l) => s + l.tons, 0));
  const subtotal = r2(allLines.reduce((s, l) => s + l.amount, 0));
  const sfxTotal = (p) => {
    const rows = allLines.filter((l) => l.plant === p);
    return { tons: r3(rows.reduce((s, l) => s + l.tons, 0)), amount: r2(rows.reduce((s, l) => s + l.amount, 0)), count: rows.length };
  };
  const sfx1 = sfxTotal("SFX 1");
  const sfx2 = sfxTotal("SFX 2");
  const bothPlants = sfx1.count > 0 && sfx2.count > 0;
  const oldBal = includeOld ? Number(oldBalance || 0) : 0;
  const discountAmt = includeDiscount ? Number(discount || 0) : 0;
  const legacyPrev = savedBill && !(savedBill.included_months || []).length ? (savedBill.previous_total || 0) : 0;
  const gstRateNum = includeGst ? Number(gstRate || 0) : 0;
  let gstAmount = 0;
  if (includeGst && gstRateNum) {
    gstAmount = gstType === "inclusive" ? r2(subtotal - subtotal / (1 + gstRateNum / 100)) : r2(subtotal * gstRateNum / 100);
  }
  const gstAdd = includeGst && gstType === "exclusive" ? gstAmount : 0;
  const grandTotal = r2(subtotal + legacyPrev + gstAdd + oldBal - discountAmt);

  const doc = {
    company: settings?.company_name || "ST FRANCIS XAVIER ICE PLANT",
    settings,
    customer: customer.trim(), month,
    rate_per_ton: rateNum,
    lines: allLines, total_tons: totalTons, subtotal,
    sfx1, sfx2, both_plants: bothPlants,
    include_gst: includeGst, gst_type: gstType, gst_rate: gstRateNum, gst_amount: gstAmount,
    legacy_previous: legacyPrev, legacy_previous_month: savedBill?.previous_month,
    include_old_balance: includeOld, old_balance: oldBal,
    include_discount: includeDiscount, discount: discountAmt,
    grand_total: grandTotal,
  };

  // Verification safety net: compare rendered records vs independent DB counts per month & plant
  const renderedByMonth = {};
  allLines.forEach((l) => {
    const mk = (l.sale_date || "").slice(0, 7);
    const b = renderedByMonth[mk] || (renderedByMonth[mk] = { sfx1: 0, sfx2: 0, other: 0, total: 0 });
    if (l.plant === "SFX 1") b.sfx1++; else if (l.plant === "SFX 2") b.sfx2++; else b.other++;
    b.total++;
  });
  const verMonths = preview?.verification?.months || [];
  const dbTotal = preview?.verification ? preview.verification.db_total : null;
  const renderedTotal = allLines.length;
  let verifyOk = dbTotal !== null;
  const verifyRows = verMonths.map((m) => {
    const rc = renderedByMonth[m.month] || { sfx1: 0, sfx2: 0, other: 0, total: 0 };
    const ok = rc.sfx1 === m.sfx1 && rc.sfx2 === m.sfx2 && rc.total === m.total;
    if (!ok) verifyOk = false;
    return { ...m, r_sfx1: rc.sfx1, r_sfx2: rc.sfx2, r_total: rc.total, ok };
  });
  if (dbTotal !== null && renderedTotal !== dbTotal) verifyOk = false;

  const saveBill = async () => {
    if (!customer.trim()) { toast.error("Select a customer"); return; }
    if (!rateNum) { toast.error("Enter the rate per ton"); return; }
    try {
      const { data } = await api.post("/bills", {
        customer: customer.trim(), month, rate_per_ton: rateNum,
        include_previous: includedMonths.length > 0, include_old_balance: includeOld,
        old_balance: oldBal, included_months: includedMonths,
        include_discount: includeDiscount, discount: discountAmt,
        include_gst: includeGst, gst_type: gstType, gst_rate: gstRateNum,
      });
      setSavedBill(data);
      toast.success("Monthly bill saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const copyExcel = async () => {
    const money = (n) => fmtMoney(n);
    const cell = (extra) => `padding:4px 12px;font-family:Calibri,Arial,sans-serif;font-size:11pt;` + (extra || "");
    const body = doc.lines.map((l) => `
      <tr>
        <td style="${cell()}">${fmtDate(l.sale_date)}</td>
        <td style="${cell("text-align:right;")}">${fmtTons(l.tons)}</td>
        <td style="${cell("text-align:right;")}">${money(l.amount)}</td>
      </tr>`).join("");
    let foot = `
      <tr>
        <td style="${cell("border-top:1.5px solid #000;font-weight:bold;")}">Total</td>
        <td style="${cell("border-top:1.5px solid #000;text-align:right;font-weight:bold;")}">${fmtTons(doc.total_tons)}</td>
        <td style="${cell("border-top:1.5px solid #000;text-align:right;font-weight:bold;")}">${money(doc.subtotal)}</td>
      </tr>`;
    if (doc.legacy_previous > 0)
      foot += `<tr><td style="${cell()}">Previous (${monthLabel(doc.legacy_previous_month)})</td><td style="${cell()}"></td><td style="${cell("text-align:right;")}">${money(doc.legacy_previous)}</td></tr>`;
    if (doc.include_old_balance && doc.old_balance)
      foot += `<tr><td style="${cell()}">Old Balance</td><td style="${cell()}"></td><td style="${cell("text-align:right;")}">${money(doc.old_balance)}</td></tr>`;
    foot += `
      <tr>
        <td style="${cell("border-top:3px double #000;font-weight:bold;")}">Total Amount Due</td>
        <td style="${cell("border-top:3px double #000;")}"></td>
        <td style="${cell("border-top:3px double #000;text-align:right;font-weight:bold;")}">${money(doc.grand_total)}</td>
      </tr>`;
    const html = `<table style="border-collapse:collapse;">
      <thead><tr>
        <th style="${cell("text-align:left;border-bottom:2px solid #000;")}">Date</th>
        <th style="${cell("text-align:right;border-bottom:2px solid #000;")}">Quantity</th>
        <th style="${cell("text-align:right;border-bottom:2px solid #000;")}">Amount</th>
      </tr></thead>
      <tbody>${body}${foot}</tbody>
    </table>`;

    const p = [["Date", "Quantity", "Amount"]];
    doc.lines.forEach((l) => p.push([fmtDate(l.sale_date), fmtTons(l.tons), money(l.amount)]));
    p.push(["Total", fmtTons(doc.total_tons), money(doc.subtotal)]);
    if (doc.legacy_previous > 0) p.push([`Previous (${monthLabel(doc.legacy_previous_month)})`, "", money(doc.legacy_previous)]);
    if (doc.include_old_balance && doc.old_balance) p.push(["Old Balance", "", money(doc.old_balance)]);
    p.push(["Total Amount Due", "", money(doc.grand_total)]);
    const plain = p.map((r) => r.join("\t")).join("\n");

    try {
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([new window.ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([plain], { type: "text/plain" }),
        })]);
      } else {
        await navigator.clipboard.writeText(plain);
      }
      toast.success("Copied for Excel — paste into a cell");
    } catch (e) {
      try { await navigator.clipboard.writeText(plain); toast.success("Copied for Excel"); }
      catch { toast.error("Copy failed"); }
    }
  };

  return (
    <div>
      <div className="no-print">
        <h1 className="font-chivo font-black text-3xl text-slate-900">Monthly Bill</h1>
        <p className="text-slate-500 mt-1 mb-6">Pick a customer & month, enter the rate per ton, then generate the bill.</p>

        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm grid gap-4 md:grid-cols-2">
          <div><Label>Customer</Label><CustomerAutocomplete value={customer} onChange={(v) => { setCustomer(v); }} testid="bill-customer-input" /></div>
          <div><Label>Month</Label><Input type="month" data-testid="bill-month-input" value={month} onChange={(e) => setMonth(e.target.value)} /></div>

          <div>
            <Label>Rate per Ton (₹)</Label>
            <Input type="number" data-testid="bill-rate-input" placeholder="e.g. 1500"
              value={rate} onChange={(e) => { setRate(e.target.value); invalidate(); }} />
          </div>
          <div className="flex items-end">
            <div className="w-full bg-slate-50 border border-slate-200 rounded-md px-4 py-2.5">
              <div className="text-xs text-slate-500">Total Tons this month</div>
              <div className="font-chivo font-black text-xl text-slate-900 tabular">{fmtTons(currentMonthTons)} t</div>
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="flex items-center gap-3">
              <Switch checked={includePrevious} onCheckedChange={(v) => { setIncludePrevious(v); if (!v) setIncludedMonths([]); invalidate(); }} data-testid="include-previous-switch" />
              <Label>Include Other Months' Transactions</Label>
            </div>
            {includePrevious && (
              <div className="mt-3 bg-slate-50 border border-slate-200 rounded-md p-3">
                <Label className="text-xs">Add a month's transactions to this bill</Label>
                <div className="mt-1.5 max-w-xs">
                  <Select value="" onValueChange={(v) => addMonth(v)}>
                    <SelectTrigger data-testid="add-month-select"><SelectValue placeholder="Select a month to include" /></SelectTrigger>
                    <SelectContent>
                      {(preview?.available_months || []).filter((m) => !includedMonths.includes(m)).map((m) => (
                        <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>
                      ))}
                      {(preview?.available_months || []).filter((m) => !includedMonths.includes(m)).length === 0 && (
                        <div className="px-2 py-1.5 text-xs text-slate-400">No other months available</div>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                {includedMonths.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3" data-testid="included-months-list">
                    {includedMonths.map((m) => (
                      <span key={m} data-testid={`included-month-chip-${m}`} className="inline-flex items-center gap-1.5 bg-sky-100 text-sky-800 text-xs font-medium rounded-full pl-3 pr-1.5 py-1">
                        {monthLabel(m)}
                        <button type="button" data-testid={`remove-month-${m}`} onClick={() => removeMonth(m)} className="hover:bg-sky-200 rounded-full p-0.5">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-xs text-slate-500 mt-2">These months' individual transactions are added to this bill and priced at the rate above. The original records are not duplicated.</p>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={includeOld} onCheckedChange={(v) => { setIncludeOld(v); invalidate(); }} data-testid="include-old-switch" />
            <Label>Include Old Balance (from Excel)</Label>
          </div>
          {includeOld && (
            <div><Label>Old Balance (₹)</Label><Input type="number" data-testid="old-balance-input" placeholder="Type amount from Excel" value={oldBalance} onChange={(e) => { setOldBalance(e.target.value); invalidate(); }} /></div>
          )}
          <div className="flex items-center gap-3">
            <Switch checked={includeDiscount} onCheckedChange={(v) => { setIncludeDiscount(v); invalidate(); }} data-testid="include-discount-switch" />
            <Label>Include Discount</Label>
          </div>
          {includeDiscount && (
            <div><Label>Discount (₹)</Label><Input type="number" data-testid="discount-input" placeholder="Amount to reduce" value={discount} onChange={(e) => { setDiscount(e.target.value); invalidate(); }} /></div>
          )}
          <div className="flex items-center gap-3">
            <Switch checked={includeGst} onCheckedChange={(v) => { setIncludeGst(v); invalidate(); }} data-testid="include-gst-switch" />
            <Label>Add GST</Label>
          </div>
          {includeGst && (
            <div className="flex gap-3">
              <div className="w-32"><Label>GST Rate (%)</Label><Input type="number" data-testid="gst-rate-input" placeholder="e.g. 18" value={gstRate} onChange={(e) => { setGstRate(e.target.value); invalidate(); }} /></div>
              <div className="flex-1"><Label>GST Type</Label>
                <Select value={gstType} onValueChange={(v) => { setGstType(v); invalidate(); }}>
                  <SelectTrigger data-testid="gst-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="exclusive">Exclusive (add on top)</SelectItem>
                    <SelectItem value="inclusive">Inclusive (already in amount)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div className="md:col-span-2 flex flex-wrap gap-3 pt-2 border-t border-slate-100">
            <Button data-testid="generate-bill-button" className="bg-sky-600 hover:bg-sky-700" onClick={saveBill} disabled={loading}>
              <Save className="h-4 w-4 mr-1" /> {savedBill ? "Update Saved Bill" : "Generate & Save Bill"}
            </Button>
            <Button variant="outline" data-testid="add-sales-button" onClick={() => { setAddForm(emptyAdd); setAddOpen(true); }} disabled={!customer.trim()}><Plus className="h-4 w-4 mr-1" /> Add Sales</Button>
            <Button variant="outline" data-testid="print-bill-button" onClick={() => window.print()} disabled={!customer.trim() || !verifyOk}><Printer className="h-4 w-4 mr-1" /> Print Bill</Button>
            <Button variant="outline" data-testid="copy-excel-button" onClick={copyExcel} disabled={!customer.trim() || !verifyOk}><Copy className="h-4 w-4 mr-1" /> Copy for Excel</Button>
            {savedBill && <span className="self-center text-xs text-emerald-600 font-medium">Saved · rate ₹{savedBill.rate_per_ton}/ton locked to this bill</span>}
          </div>
        </div>
      </div>

      {customer.trim() && preview?.verification && (
        <div className="no-print mt-6 max-w-3xl" data-testid="bill-verification">
          <div className={`rounded-lg border p-4 ${verifyOk ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-300"}`}>
            <div className="flex items-center gap-2 font-semibold text-sm mb-2">
              {verifyOk ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-red-600" />}
              <span className={verifyOk ? "text-emerald-800" : "text-red-800"} data-testid="verification-status">Data Verification {verifyOk ? "Passed" : "Failed"}</span>
            </div>
            <div className="text-xs text-slate-600 mb-2">Selected period: {verifyRows.map((r) => r.label).join(", ")}</div>
            <table className="text-xs w-full max-w-md" data-testid="verification-table">
              <thead className="text-slate-500 text-left"><tr><th className="py-1 pr-4">Month</th><th className="py-1 pr-4 text-right">SFX 1</th><th className="py-1 pr-4 text-right">SFX 2</th><th className="py-1 text-right">Records</th></tr></thead>
              <tbody>
                {verifyRows.map((r) => (
                  <tr key={r.month} className={r.ok ? "" : "text-red-700 font-semibold"}>
                    <td className="py-0.5 pr-4">{r.label}</td>
                    <td className="py-0.5 pr-4 text-right tabular">{r.r_sfx1}{r.ok ? "" : ` / ${r.sfx1}`}</td>
                    <td className="py-0.5 pr-4 text-right tabular">{r.r_sfx2}{r.ok ? "" : ` / ${r.sfx2}`}</td>
                    <td className="py-0.5 text-right tabular">{r.r_total}</td>
                  </tr>
                ))}
                <tr className="border-t border-slate-300 font-bold"><td className="py-1 pr-4">Total Records</td><td /><td /><td className="py-1 text-right tabular">{renderedTotal}{verifyOk ? "" : ` / ${dbTotal}`}</td></tr>
              </tbody>
            </table>
            {!verifyOk && <p className="text-xs text-red-700 mt-2 font-medium" data-testid="verification-error">Data verification failed. Some selected records are missing or incorrectly grouped. Please refresh and try again. Print & Copy are disabled until this is resolved.</p>}
          </div>
        </div>
      )}

      {customer.trim() && (
        <div className="print-area mt-6 bg-white border border-slate-200 rounded-lg p-8 max-w-3xl shadow-sm" data-testid="bill-preview">
          <div className="text-center border-b-2 border-slate-900 pb-4 mb-6">
            <h2 className="font-chivo font-black text-2xl text-slate-900">{doc.company}</h2>
            {doc.settings && (
              <div className="text-xs text-slate-600 mt-1 space-y-0.5" data-testid="bill-business-details">
                {doc.settings.show_address && doc.settings.address && <div>{doc.settings.address}</div>}
                <div className="flex justify-center gap-x-4 flex-wrap">
                  {doc.settings.show_mobile && doc.settings.mobile && <span>Mob: {doc.settings.mobile}</span>}
                  {doc.settings.show_email && doc.settings.email && <span>{doc.settings.email}</span>}
                </div>
                {doc.settings.show_gstin && doc.settings.gstin && <div>GSTIN: {doc.settings.gstin}</div>}
              </div>
            )}
            <div className="text-lg font-bold tracking-[0.3em] text-slate-700 mt-2">MONTHLY BILL</div>
          </div>
          <div className="flex justify-between mb-6 text-sm">
            <div><span className="text-slate-500">Customer:</span> <span className="font-bold">{doc.customer}</span></div>
            <div className="text-right">
              <div><span className="text-slate-500">Billing Period:</span> <span className="font-bold">{monthLabel(doc.month)}</span></div>
              <div className="text-slate-500 text-xs mt-1">Date: {fmtDate(new Date().toISOString().slice(0, 10))}</div>
            </div>
          </div>

          <table className="w-full text-sm border border-slate-300 bill-table">
            <colgroup>
              <col style={{ width: "24%" }} />
              <col style={{ width: "16%" }} />
              <col style={{ width: "38%" }} />
              <col style={{ width: "22%" }} />
            </colgroup>
            <thead className="bg-slate-100">
              <tr>
                <th className="px-3 py-2 text-left border-b border-slate-300">Date</th>
                <th className="px-3 py-2 pr-8 text-right border-b border-slate-300">Quantity</th>
                <th className="px-3 py-2 pl-6 text-left border-b border-slate-300">Vehicle No.</th>
                <th className="px-3 py-2 text-right border-b border-slate-300">Amount</th>
                <th className="px-2 border-b border-slate-300 no-print"></th>
              </tr>
            </thead>
            <tbody>
              {doc.lines.map((l, i) => {
                const showSep = doc.both_plants && (i === 0 || doc.lines[i - 1].plant !== l.plant);
                return (
                  <Fragment key={i}>
                    {showSep && (
                      <tr data-testid={`sfx-separator-${l.plant?.replace(/\s+/g, "-").toLowerCase()}`}>
                        <td colSpan={4} className="px-3 pt-1.5 pb-0.5">
                          <div className="flex items-center gap-3 text-[11px] font-semibold tracking-[0.2em] text-slate-500">
                            <span className="flex-1 border-t border-slate-300" />
                            {l.plant}
                            <span className="flex-1 border-t border-slate-300" />
                          </div>
                        </td>
                        <td className="no-print" />
                      </tr>
                    )}
                    <tr className="border-b border-slate-200">
                      <td className="px-3 py-2 whitespace-nowrap">{fmtDate(l.sale_date)}</td>
                      <td className="px-3 py-2 pr-8 text-right tabular whitespace-nowrap">{fmtTons(l.tons)} tons</td>
                      <td className="px-3 py-2 pl-6 whitespace-nowrap">{l.vehicle_no}</td>
                      <td className="px-3 py-2 text-right tabular whitespace-nowrap">{fmtMoney(l.amount)}</td>
                      <td className="px-2 text-right no-print whitespace-nowrap">
                        {l.id && (
                          <div className="inline-flex items-center gap-0.5">
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-slate-500 hover:text-sky-600"
                              data-testid={`bill-edit-sale-${l.id}`}
                              onClick={() => setEdit({ id: l.id, sale_date: l.sale_date, tons: l.tons, vehicle_no: l.vehicle_no || "" })}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-slate-500 hover:text-red-600"
                              data-testid={`bill-delete-sale-${l.id}`}
                              onClick={() => setDelTarget({ id: l.id, sale_date: l.sale_date, tons: l.tons, vehicle_no: l.vehicle_no || "" })}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  </Fragment>
                );
              })}
              {doc.lines.length === 0 && <tr><td colSpan={5} className="px-3 py-4 text-center text-slate-400">No sales this month.</td></tr>}
              <tr className="bg-slate-50 font-bold">
                <td className="px-3 py-2">TOTAL</td>
                <td className="px-3 py-2 pr-8 text-right tabular whitespace-nowrap">{fmtTons(doc.total_tons)} tons</td>
                <td className="pl-6"></td>
                <td className="px-3 py-2 text-right tabular whitespace-nowrap">{fmtMoney(doc.subtotal)}</td>
                <td className="no-print"></td>
              </tr>
            </tbody>
          </table>

          <div className="mt-6 ml-auto max-w-xs space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-slate-600">Total Tons</span><span className="tabular font-semibold">{fmtTons(doc.total_tons)} t</span></div>
            <div className="flex justify-between"><span className="text-slate-600">Rate</span><span className="tabular font-semibold">{fmtMoney(doc.rate_per_ton)}/ton</span></div>
            <div className="flex justify-between"><span className="text-slate-600">Sub Total</span><span className="tabular font-semibold">{fmtMoney(doc.subtotal)}</span></div>
            {doc.both_plants && <div className="flex justify-between text-xs text-slate-500"><span>SFX 1 Total ({fmtTons(doc.sfx1.tons)} t)</span><span className="tabular">{fmtMoney(doc.sfx1.amount)}</span></div>}
            {doc.both_plants && <div className="flex justify-between text-xs text-slate-500"><span>SFX 2 Total ({fmtTons(doc.sfx2.tons)} t)</span><span className="tabular">{fmtMoney(doc.sfx2.amount)}</span></div>}
            {doc.include_gst && doc.gst_rate > 0 && <div className="flex justify-between" data-testid="bill-gst-line"><span className="text-slate-600">GST @ {doc.gst_rate}%{doc.gst_type === "inclusive" ? " (incl.)" : ""}</span><span className="tabular font-semibold">{fmtMoney(doc.gst_amount)}</span></div>}
            {doc.legacy_previous > 0 && <div className="flex justify-between"><span className="text-slate-600">Previous Bill ({monthLabel(doc.legacy_previous_month)})</span><span className="tabular font-semibold">{fmtMoney(doc.legacy_previous)}</span></div>}
            {doc.include_old_balance && doc.old_balance > 0 && <div className="flex justify-between"><span className="text-slate-600">Old Balance</span><span className="tabular font-semibold">{fmtMoney(doc.old_balance)}</span></div>}
            {doc.include_discount && doc.discount > 0 && <div className="flex justify-between text-emerald-700"><span>Discount</span><span className="tabular font-semibold">− {fmtMoney(doc.discount)}</span></div>}
            <div className="flex justify-between border-t-2 border-slate-900 pt-2 mt-2 text-base font-black">
              <span>Total Amount Due</span><span className="tabular">{fmtMoney(doc.grand_total)}</span>
            </div>
          </div>

          <p className="mt-10 text-center text-xs text-slate-400">Thank you for your business.</p>
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-sales-dialog">
          <DialogHeader><DialogTitle>Add Sale — {customer.trim()}</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-2">
            <div><Label>Date</Label><Input type="date" data-testid="add-sale-date" value={addForm.sale_date} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setAddForm({ ...addForm, sale_date: e.target.value })} /></div>
            <div><Label>Quantity (tons)</Label><Input type="number" step="0.001" data-testid="add-sale-tons" value={addForm.tons} onChange={(e) => setAddForm({ ...addForm, tons: e.target.value })} /></div>
            <div><Label>Vehicle No.</Label><Input data-testid="add-sale-vehicle" value={addForm.vehicle_no} onChange={(e) => setAddForm({ ...addForm, vehicle_no: e.target.value })} /></div>
            <div><Label>Plant</Label>
              <Select value={addForm.plant} onValueChange={(v) => setAddForm({ ...addForm, plant: v })}>
                <SelectTrigger data-testid="add-sale-plant"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="SFX 1">SFX 1</SelectItem><SelectItem value="SFX 2">SFX 2</SelectItem></SelectContent>
              </Select>
            </div>
            <p className="text-xs text-slate-500">The amount is calculated from this bill's rate per ton. This creates one real sales record used everywhere.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} data-testid="add-sale-cancel">Cancel</Button>
            <Button className="bg-sky-600 hover:bg-sky-700" onClick={addSale} data-testid="add-sale-save">Add Sale</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="bill-edit-sale-dialog">
          <DialogHeader><DialogTitle>Edit Sale</DialogTitle></DialogHeader>
          {edit && (
            <div className="grid gap-4 py-2">
              <div><Label>Date</Label><Input type="date" data-testid="bill-edit-sale-date" value={edit.sale_date} onChange={(e) => setEdit({ ...edit, sale_date: e.target.value })} /></div>
              <div><Label>Quantity (tons)</Label><Input type="number" step="0.001" data-testid="bill-edit-sale-tons" value={edit.tons} onChange={(e) => setEdit({ ...edit, tons: e.target.value })} /></div>
              <div><Label>Vehicle No.</Label><Input data-testid="bill-edit-sale-vehicle" value={edit.vehicle_no} onChange={(e) => setEdit({ ...edit, vehicle_no: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)} data-testid="bill-edit-sale-cancel">Cancel</Button>
            <Button className="bg-sky-600 hover:bg-sky-700" onClick={saveLine} data-testid="bill-edit-sale-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent data-testid="bill-delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this transaction?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this transaction? This will remove it from the bill and all related records.
              {delTarget && <span className="block mt-2 font-medium text-slate-700">{fmtDate(delTarget.sale_date)} · {fmtTons(delTarget.tons)} tons{delTarget.vehicle_no ? ` · ${delTarget.vehicle_no}` : ""}</span>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bill-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={deleteLine} data-testid="bill-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
